import React, { useEffect } from "react";
import { Box, Typography, Container, Grid, Button, Link } from "@mui/material";
import { motion } from "framer-motion";
import heroBg from "../../assets/nextix/business-fintech-banner.jpg";
import cityWideWhitepaper from '../../City-wide-whitepaper.pdf';
import globalVideoWhitepaper from '../../Global-Video-whitepaper.pdf';
import ipPhonesWhitepaper from '../../IP-Phones.pdf';
import ofwContactCenterWhitepaper from '../../OFW-Contact-Center.pdf';

const FintechScreen = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <Box sx={{ backgroundColor: "#fff" }}>
            {/* BANNER IMAGE - Full image display in container */}
            <Container maxWidth="lg" sx={{ pt: 12, pb: 4 }}>
                <Box sx={{
                    borderRadius: "8px",
                    overflow: "hidden"
                }}>
                    <img
                        src={heroBg}
                        alt="Fintech - Secure Payment Solutions"
                        style={{
                            width: "100%",
                            height: "auto",
                            display: "block"
                        }}
                    />
                </Box>
            </Container>

            {/* MAIN CONTENT */}
            <Container maxWidth="lg" sx={{ py: 8 }}>
                <Grid container spacing={{ xs: 2, md: 6 }}>
                    {/* Left Column - Main Content */}
                    <Grid item xs={12} md={8}>
                        {/* Main Fintech Section */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: "#375ca8", textAlign: "start" }}>
                                Fintech
                            </Typography>
                        </motion.div>

                        {/* e-Wallet */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                        >
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: "#375ca8", textAlign: "start" }}>
                                e-Wallet
                            </Typography>
                            <Typography paragraph sx={{ color: "#666", lineHeight: 1.8, mb: 4, textAlign: "start" }}>
                                Nextix e-wallet is a digital wallet that allows you to make electronic transactions. You can purchase items online easily or buy in physical stores using your e-wallet without compromising security and convenience.
                            </Typography>
                        </motion.div>

                        {/* Payment Gateway */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                        >
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: "#375ca8", textAlign: "start" }}>
                                Payment Gateway
                            </Typography>
                            <Typography paragraph sx={{ color: "#666", lineHeight: 1.8, mb: 4, textAlign: "start" }}>
                                Nextix Payment gateway is an electronic payment and collection system that makes payments easy. The payment gateway can also automatically distribute to predefined bank accounts during settlement.
                            </Typography>
                        </motion.div>
                    </Grid>

                    {/* Right Column - Sidebar */}
                    <Grid item xs={12} md={4}>
                        <Box sx={{ position: { md: "sticky" }, top: 100, textAlign: 'start' }}>
                            {/* Send Us A Message */}
                            <motion.div
                                initial={{ opacity: 0, x: 30 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6 }}
                            >
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: "#375ca8" }}>
                                    Send Us A Message
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 3, color: "#666", lineHeight: 1.6 }}>
                                    For more inquiries about our Products, send us a message
                                </Typography>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    href="mailto:info@nextix.co"
                                    sx={{
                                        backgroundColor: "#82c9ed",
                                        color: "white",
                                        fontWeight: 700,
                                        py: 1.5,
                                        mb: 4,
                                        "&:hover": { backgroundColor: "#6bb5d9" }
                                    }}
                                >
                                    CONTACT OUR SALES NOW
                                </Button>
                            </motion.div>

                            {/* Contact Information */}
                            <motion.div
                                initial={{ opacity: 0, x: 30 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: 0.1 }}
                            >
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: "#1c1c1c" }}>
                                    Email Us:
                                </Typography>

                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" sx={{ color: "#666", mb: 1 }}>
                                        <strong>Inquiry:</strong>{" "}
                                        <Link href="mailto:info@nextix.co" sx={{ color: "#375ca8", textDecoration: "none" }}>
                                            info@nextix.co
                                        </Link>
                                    </Typography>
                                </Box>

                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" sx={{ color: "#666", mb: 1 }}>
                                        <strong>Support:</strong>{" "}
                                        <Link href="mailto:helpdesk@nextix.co" sx={{ color: "#375ca8", textDecoration: "none" }}>
                                            helpdesk@nextix.co
                                        </Link>
                                    </Typography>
                                </Box>

                                <Box sx={{ mb: 4 }}>
                                    <Typography variant="body2" sx={{ color: "#666", mb: 1 }}>
                                        <strong>Privacy:</strong>{" "}
                                        <Link href="mailto:dpo@nextix.co" sx={{ color: "#375ca8", textDecoration: "none" }}>
                                            dpo@nextix.co
                                        </Link>
                                    </Typography>
                                </Box>

                                {/* White Paper Resources */}
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: "#1c1c1c" }}>
                                    White Paper
                                </Typography>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                    <Link href={ofwContactCenterWhitepaper} target="_blank" rel="noopener noreferrer" sx={{ color: "#375ca8", textDecoration: "none", fontSize: "0.875rem", "&:hover": { textDecoration: "none", color: '#5bc0de' } }}>
                                        OFW-Contact-Center
                                    </Link>
                                    <Link href={globalVideoWhitepaper} target="_blank" rel="noopener noreferrer" sx={{ color: "#375ca8", textDecoration: "none", fontSize: "0.875rem", "&:hover": { textDecoration: "none", color: '#5bc0de' } }}>
                                        Global-Video-whitepaper
                                    </Link>
                                    <Link href={cityWideWhitepaper} target="_blank" rel="noopener noreferrer" sx={{ color: "#375ca8", textDecoration: "none", fontSize: "0.875rem", "&:hover": { textDecoration: "none", color: '#5bc0de' } }}>
                                        City-wide-whitepaper
                                    </Link>
                                    {/* <Link href={ipPhonesWhitepaper} target="_blank" rel="noopener noreferrer" sx={{ color: "#375ca8", textDecoration: "none", fontSize: "0.875rem", "&:hover": { textDecoration: "none", color: '#5bc0de' } }}>
                                        IP-Phones
                                    </Link> */}
                                </Box>
                            </motion.div>
                        </Box>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default FintechScreen;
